/**
 * Access Control Panel Integration — page JS
 * Handles: carousel prev/next scrolling, FAQ accordion, quote modal
 * open/close, and the direct-POST HubSpot form submission.
 *
 * Loaded with `defer` from the section file, scoped by [data-acpi-root]
 * so it's safe if this section is ever duplicated on a page (though
 * that shouldn't happen for a single landing page).
 */
(function () {
  'use strict';

  document.querySelectorAll('[data-acpi-root]').forEach(function (root) {
    initHeroCarousel(root);
    initCarousels(root);
    initFaq(root);
    initQuoteModal(root);
    initIncludedCardHeights(root);
  });

  // RFQ Checklist is its own scoped root ([data-acpi-rfq-root]) nested
  // inside the main page, since it has its own HubSpot portal/form IDs
  // on its own wrapping element.
  document.querySelectorAll('[data-acpi-rfq-root]').forEach(function (rfqRoot) {
    initRfqCardToggle(rfqRoot);
    initRfqStepper(rfqRoot);
    initRfqSubmit(rfqRoot);
  });

  // ── Hero image carousel (auto-rotating crossfade, matches mockup) ──
  function initHeroCarousel(root) {
    var carousel = root.querySelector('.js-acpi-hero-carousel');
    if (!carousel) return;
    var slides = carousel.querySelectorAll('.acpi-hero-slide');
    if (slides.length < 2) return;
    var dots = root.querySelectorAll('.js-acpi-hero-dots span');
    var current = 0;
    setInterval(function () {
      slides[current].classList.remove('is-active');
      if (dots[current]) dots[current].classList.remove('is-active');
      current = (current + 1) % slides.length;
      slides[current].classList.add('is-active');
      if (dots[current]) dots[current].classList.add('is-active');
    }, 4000);
  }

  // ── Carousels ──────────────────────────────────────────────────
  function initCarousels(root) {
    var tracks = root.querySelectorAll('.js-acpi-carousel');
    tracks.forEach(function (track) {
      var key = track.getAttribute('data-carousel');
      var prevBtn = root.querySelector('.js-acpi-carousel-prev[data-carousel="' + key + '"]');
      var nextBtn = root.querySelector('.js-acpi-carousel-next[data-carousel="' + key + '"]');

      function scrollByCard(direction) {
        var card = track.querySelector('.acpi-card');
        var amount = card ? card.getBoundingClientRect().width + 26 : 320;
        track.scrollBy({ left: direction * amount, behavior: 'smooth' });
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { scrollByCard(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { scrollByCard(1); });
    });
  }

  // ── FAQ accordion ──────────────────────────────────────────────
  function initFaq(root) {
    var items = root.querySelectorAll('.js-acpi-faq-item');
    items.forEach(function (item) {
      var toggle = item.querySelector('.js-acpi-faq-toggle');
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        item.classList.toggle('is-open');
      });
    });
  }

  // ── Quote modal ────────────────────────────────────────────────
  // ── "What's Included" card description height matching ────────────
  // Each card's bullet list needs to start at the same vertical position
  // regardless of how long that card's own description text is. CSS
  // alone can't match one nested element's height to its siblings'
  // across separate card containers (that needs CSS subgrid, which
  // doesn't have reliable enough support yet for a public storefront),
  // so this measures the tallest description on load/resize and sets
  // that as a shared min-height on all of them. Re-run on resize since
  // text reflows at different widths change how many lines each
  // description wraps to.
  function initIncludedCardHeights(root) {
    var descs = root.querySelectorAll('.acpi-included-desc');
    if (!descs.length) return;

    function equalize() {
      descs.forEach(function (d) { d.style.minHeight = ''; });
      var tallest = 0;
      descs.forEach(function (d) {
        if (d.offsetHeight > tallest) tallest = d.offsetHeight;
      });
      descs.forEach(function (d) { d.style.minHeight = tallest + 'px'; });
    }

    equalize();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(equalize, 150);
    });
  }

  function initQuoteModal(root) {
    var overlay = root.querySelector('.js-acpi-quote-modal-overlay');
    if (!overlay) return;
    var closeBtn = overlay.querySelector('.js-acpi-quote-modal-close');
    var triggers = root.querySelectorAll('.js-acpi-quote-cta');

    function openModal(e) {
      if (e) e.preventDefault();
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var firstField = overlay.querySelector('input, textarea');
      if (firstField) firstField.focus();
    }

    function closeModal() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', openModal);
    });
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal();
    });

    // "Download the RFQ Checklist" links (inside the modal and in the
    // CTA band) — close the quote modal if it's open, then let the browser
    // handle the download (or, for any remaining #anchor links, scroll to
    // the target section instead).
    document.querySelectorAll('.js-acpi-modal-rfq-link').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = link.getAttribute('href') || '';
        if (overlay.classList.contains('is-open')) closeModal();
        if (href.charAt(0) !== '#') return; // real file link — let the browser download it natively
        var target = document.querySelector(href);
        if (!target) return; // let the browser try its default behaviour
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    initQuoteForm(root, overlay);
  }

  // ── HubSpot form submission (direct POST, no reCAPTCHA) ─────────
  function initQuoteForm(root, overlay) {
    var form = overlay.querySelector('.js-acpi-quote-form');
    if (!form) return;
    var statusEl = overlay.querySelector('.js-acpi-form-status');

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // Honeypot check — if filled, silently drop the submission.
      var hp = form.querySelector('.acpi-hp-input');
      if (hp && hp.value) return;

      var portalId = form.getAttribute('data-hs-portal-id');
      var formId = form.getAttribute('data-hs-form-id');

      if (!portalId || !formId) {
        setStatus('This form isn\'t connected yet — please email sales@powerbox.co.nz directly.', true);
        return;
      }

      var fields = [];
      form.querySelectorAll('input[name], textarea[name], select[name]').forEach(function (field) {
        if (field.classList.contains('acpi-hp-input')) return;
        fields.push({ name: field.name, value: field.value });
      });

      var payload = {
        fields: fields,
        context: {
          pageUri: window.location.href,
          pageName: document.title
        }
      };
      // hutk deliberately excluded — including it here has previously
      // caused duplicate submissions on other Powerbox forms.

      var submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      setStatus('Sending…', false);

      fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + portalId + '/' + formId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HubSpot submission failed');
          form.reset();
          setStatus('Thank you for submitting the form. A Powerbox team member will contact you soon.', false);
        })
        .catch(function () {
          setStatus('Something went wrong sending that — please try again or email sales@powerbox.co.nz.', true);
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    });

    function setStatus(message, isError) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.style.color = isError ? '#DB2727' : '#58595B';
    }
  }

// ── RFQ Checklist: collapsible card toggle ──
  function initRfqCardToggle(root) {
    var card = root.querySelector('.js-acpi-rfq-card');
    var toggles = root.querySelectorAll('.js-acpi-rfq-toggle');
    if (!card || !toggles.length) return;
    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        card.classList.toggle('is-open');
      });
    });
  }

  // ── RFQ Checklist: stepper navigation ──
  function initRfqStepper(root) {
    var stepper = root.querySelector('.js-acpi-rfq-stepper');
    if (!stepper) return;
    var totalSteps = parseInt(stepper.getAttribute('data-total-steps'), 10) || 1;
    var dots = root.querySelectorAll('.js-acpi-rfq-dot');
    var panels = root.querySelectorAll('.acpi-rfq-panel');
    var backBtn = root.querySelector('.js-acpi-rfq-back');
    var nextBtn = root.querySelector('.js-acpi-rfq-next');
    var submitBtn = root.querySelector('.js-acpi-rfq-submit');
    var current = 1;

    function goToStep(step) {
      if (step < 1 || step > totalSteps) return;
      current = step;

      panels.forEach(function (panel) {
        panel.classList.toggle('is-active', parseInt(panel.getAttribute('data-panel'), 10) === current);
      });

      dots.forEach(function (dot) {
        var dotStep = parseInt(dot.getAttribute('data-step'), 10);
        dot.classList.toggle('is-active', dotStep === current);
        dot.classList.toggle('is-complete', dotStep < current);
        if (dotStep === current) {
          dot.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });

      if (backBtn) backBtn.style.display = current === 1 ? 'none' : '';
      if (nextBtn) nextBtn.style.display = current === totalSteps ? 'none' : '';
      if (submitBtn) submitBtn.style.display = current === totalSteps ? '' : 'none';

      var content = root.querySelector('.acpi-rfq-content');
      if (content) content.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goToStep(parseInt(dot.getAttribute('data-step'), 10));
      });
    });

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        // Step 1 holds the only required fields — check native validity before advancing.
        if (current === 1) {
          var panel1 = root.querySelector('.acpi-rfq-panel[data-panel="1"]');
          var invalidField = panel1 ? panel1.querySelector(':invalid') : null;
          if (invalidField) {
            invalidField.reportValidity();
            return;
          }
        }
        goToStep(current + 1);
      });
    }

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        goToStep(current - 1);
      });
    }

    goToStep(1);
  }

  // ── RFQ Checklist: HubSpot submission ──
  // Walks the RFQ form's panels (skipping core identity fields) and builds
  // one readable, formatted text block — panel headings as section titles,
  // "Label: value" lines underneath, radio/checkbox groups collapsed to
  // their selected option text. Empty fields are omitted entirely.
  function buildProjectDetailsText(form) {
    var sections = [];

    form.querySelectorAll('.acpi-rfq-panel').forEach(function (panel) {
      var headingEl = panel.querySelector('.acpi-rfq-panel-heading');
      var heading = headingEl ? headingEl.textContent.trim() : '';
      var lines = [];

      panel.querySelectorAll('.acpi-rfq-field').forEach(function (fieldWrap) {
        var input = fieldWrap.querySelector('input, textarea');
        if (!input || !input.name) return;
        if (input.getAttribute('data-rfq-core') === 'true') return;
        if (input.classList.contains('acpi-rfq-hp-input')) return;
        var labelEl = fieldWrap.querySelector('label');
        var label = labelEl ? labelEl.textContent.replace('*', '').trim() : input.name;
        var value = input.value ? input.value.trim() : '';
        if (value) lines.push(label + ': ' + value);
      });

      panel.querySelectorAll('.acpi-rfq-choice-block').forEach(function (block) {
        var qEl = block.querySelector('.acpi-rfq-choice-question');
        var question = qEl ? qEl.textContent.trim() : '';
        var selected = [];
        block.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked').forEach(function (input) {
          var labelEl = input.closest('label');
          selected.push(labelEl ? labelEl.textContent.trim() : input.value);
        });
        if (selected.length) lines.push(question + ': ' + selected.join(', '));
      });

      if (lines.length) {
        sections.push(heading + '\n' + lines.map(function (l) { return '- ' + l; }).join('\n'));
      }
    });

    return sections.join('\n\n');
  }

  function initRfqSubmit(root) {
    var form = root.querySelector('.js-acpi-rfq-form');
    if (!form) return;
    var statusEl = root.querySelector('.js-acpi-rfq-status');
    var successEl = root.querySelector('.js-acpi-rfq-success');

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var hp = form.querySelector('.acpi-rfq-hp-input');
      if (hp && hp.value) return;

      var portalId = root.getAttribute('data-hs-portal-id');
      var formId = root.getAttribute('data-hs-form-id');

      if (!portalId || !formId) {
        setStatus('This form isn\'t connected yet — please email sales@powerbox.co.nz with your requirements.', true);
        return;
      }

      var fields = [];

      // Core identity fields (First/Last Name, Company, Email, Phone) map to
      // real HubSpot contact properties, so they're sent individually.
      form.querySelectorAll('[data-rfq-core="true"]').forEach(function (field) {
        if (field.value) fields.push({ name: field.name, value: field.value.trim() });
      });

      // Everything else — project details plus every technical answer from
      // Steps 2–8 — is folded into one readable, formatted note rather than
      // ~30 separate contact properties. This avoids overwriting a repeat
      // customer's previous submission data, and reads like the original
      // PDF checklist for whoever picks up the quote.
      var detailsText = buildProjectDetailsText(form);
      if (detailsText) fields.push({ name: 'rfq_project_details', value: detailsText });

      var payload = {
        fields: fields,
        context: {
          pageUri: window.location.href,
          pageName: document.title
        }
      };

      var submitBtn = form.querySelector('.js-acpi-rfq-submit');
      if (submitBtn) submitBtn.disabled = true;
      setStatus('Sending…', false);

      fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + portalId + '/' + formId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HubSpot submission failed');
          form.style.display = 'none';
          var stepperEl = root.querySelector('.js-acpi-rfq-stepper');
          if (stepperEl) stepperEl.style.display = 'none';
          if (successEl) successEl.classList.add('is-shown');
        })
        .catch(function () {
          setStatus('Something went wrong sending that — please try again or email sales@powerbox.co.nz.', true);
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
    });

    function setStatus(message, isError) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.style.color = isError ? '#DB2727' : '#58595B';
    }
  }
})();
